import React, { useState, useEffect, forwardRef } from "react";
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Button,
    Slide,
    SlideProps,
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const Transition = forwardRef(function Transition(
    props: SlideProps,
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface MonthlyFilterProps {
    selectedMonth: string;
    setSelectedMonth: (month: string) => void;
    selectedYear: number;
    setSelectedYear: (year: number) => void;
    availableYears: number[];
    availableMonths: string[];
}

const MonthlyFilter: React.FC<MonthlyFilterProps> = ({
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    availableYears,
    availableMonths
}) => {
    const [open, setOpen] = useState(false);
    const [localMonth, setLocalMonth] = useState(selectedMonth);
    const [localYear, setLocalYear] = useState(selectedYear);


    const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        );
    // Reset local state when dropdown options change
    useEffect(() => {
        if (availableMonths.length && availableMonths.includes(localMonth.toLowerCase())) {
            setLocalMonth(localMonth);
        } else if (availableMonths.length) {
            setLocalMonth(
                availableMonths[0].charAt(0).toUpperCase() + availableMonths[0].slice(1)
            );
        }

        if (availableYears.length && availableYears.includes(localYear)) {
            setLocalYear(localYear);
        } else if (availableYears.length) {
            setLocalYear(availableYears[0]);
        }
    }, [availableMonths, availableYears]);

    const handleApply = () => {
        setSelectedMonth(localMonth);
        setSelectedYear(localYear);
        setOpen(false);
    };

    return (
        <>
            <Box sx={{ mt: 0 }}>
                <Button
                    variant="outlined"
                    onClick={() => setOpen(true)}
                    endIcon={<ExpandMoreIcon />}
                    sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#333",
                        borderRadius: 2,
                        border: "1px solid #DADADA",
                        px: 1.5,
                        py: 0.5,
                        textTransform: "none",
                        backgroundColor: "#fff",
                        height: "40px",
                    }}
                >
                    {selectedMonth} {selectedYear}
                </Button>
            </Box>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                TransitionComponent={Transition}
                keepMounted
                fullWidth
                maxWidth="xs"
                sx={{
                    "& .MuiDialog-paper": {
                        margin: 0,
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        width: "100%",
                        background: "none",
                        padding: "0",
                        maxWidth: "100%",
                    },
                }}
            >
                <DialogContent sx={{ padding: "0" }}>
                    <Box display="flex" justifyContent="center">
                        <IconButton onClick={() => setOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            width: "auto",
                            background: "#fff",
                            padding: "20px",
                            borderTopLeftRadius: "20px",
                            borderTopRightRadius: "20px",
                            //  paddingBottom: "130px",
                        }}
                    >
                        <Typography variant="h6" align="center" fontWeight="bold" mt={1} mb={3}>
                            Filter by Month
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1 }}>
                            {/* Year Selector */}
                            <Box sx={{ width: "50%" }}>
                                <Typography fontSize={13} fontWeight={500} mb={0.5}>Year</Typography>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <Select
                                        value={localYear}
                                        onChange={(e) => setLocalYear(Number(e.target.value))}
                                        sx={{ height: 44, borderRadius: 2, backgroundColor: "#F9F9F9", fontSize: "14px" }}
                                    >
                                        {availableYears.map((year) => (
                                            <MenuItem key={year} value={year}>{year}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Month Selector */}
                            <Box sx={{ width: "50%" }}>
                                <Typography fontSize={13} fontWeight={500} mb={0.5}>Month</Typography>
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <Select
                                        value={localMonth}
                                        onChange={(e) => setLocalMonth(e.target.value as string)}
                                        sx={{ height: 44, borderRadius: 2, backgroundColor: "#F9F9F9", fontSize: "14px" }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 200, // ✅ Limit dropdown height
                                                    minHeight: "auto",
                                                },
                                            },
                                        }}
                                    >
                                        {availableMonths.map((month) => (
                                            <MenuItem key={month} value={month.charAt(0).toUpperCase() + month.slice(1)}>
                                                {month.charAt(0).toUpperCase() + month.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        <Button
                            variant="contained"
                            fullWidth
                            sx={{
                                backgroundColor: "#E53935 !important",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "15px",
                                borderRadius: 2,
                                py: 1,
                                mb: isMobileDevice ? "120px" : 0,
                            }}
                            onClick={handleApply}
                        >
                            Apply
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MonthlyFilter;